import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { APP_PATHS } from '../config/app-paths';
import { Organizacja } from '../../shared/models/organizacja';
import { SpinnerService } from './spinner.service';
import { AuthApi } from '../../shared/services/api/auth.api';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { LastUsedApplicationService } from './last-used-application.service';
import { Application } from '../../shared/models/application';
import { HttpResponseService } from './http-error-response.service';
import { Beneficjent } from '../../shared/models/beneficjent';
import { ApplicationContextDto } from '../../shared/models/wybrana-organizacja';

export enum ApplicationContext {
  BENEFICIARY = 'BENEFICIARY',
  ORGANIZATION = 'ORGANIZATION',
  ADMINISTRATOR = 'ADMINISTRATOR',
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationContextService {
  readonly #authApi = inject(AuthApi);
  readonly #router = inject(Router);
  readonly #spinnerService = inject(SpinnerService);
  readonly #lastUsedApplicationService = inject(LastUsedApplicationService);
  readonly #httpResponseService = inject(HttpResponseService);

  #applicationContext = signal<ApplicationContext>(
    ApplicationContext.BENEFICIARY,
  );
  readonly applicationContext = this.#applicationContext.asReadonly();
  #organizationFromContext = signal<Organizacja | null>(null);

  public handleContextOnInit(loggedUser?: Beneficjent): void {
    let context = loggedUser?.applicationContextOnInit;
    if (!context) {
      context = ApplicationContext.BENEFICIARY;
    }
    this.#applicationContext.set(context);

    if (ApplicationContext.ORGANIZATION === context) {
      const wybranaOrganizacja = loggedUser?.organizacje?.find(
        (org) => org.id === loggedUser?.wybranaOrganizacjaId,
      );

      if (wybranaOrganizacja) {
        this.#organizationFromContext.set(wybranaOrganizacja);
      }
    }
  }

  public switchToOrganization(
    organization: Organizacja,
    redirectToHomePage: boolean = true,
  ): void {
    this.#changeApplicationContext(
      ApplicationContext.ORGANIZATION,
      organization,
      redirectToHomePage,
    );
  }

  public switchToBeneficiary(redirectToHomePage: boolean = true): void {
    this.#changeApplicationContext(
      ApplicationContext.BENEFICIARY,
      null,
      redirectToHomePage,
    );
  }

  public switchToAdmin(): void {
    this.#organizationFromContext.set(null);
    this.#applicationContext.set(ApplicationContext.ADMINISTRATOR);
    this.#changeApplicationContext(
      ApplicationContext.ADMINISTRATOR,
      null,
      false,
    );
    this.#router.navigate([APP_PATHS.admin.root]);
  }

  public getOrganizationFromContext(): Organizacja | null {
    return this.isOrganizationContext()
      ? this.#organizationFromContext()
      : null;
  }

  public isOrganizationContext(): boolean {
    return this.#applicationContext() === ApplicationContext.ORGANIZATION;
  }

  public isBeneficiaryContext(): boolean {
    return this.#applicationContext() === ApplicationContext.BENEFICIARY;
  }

  public isAdminContext(): boolean {
    return this.#applicationContext() === ApplicationContext.ADMINISTRATOR;
  }

  #changeApplicationContext(
    applicationContext: ApplicationContext,
    organization: Organizacja | null,
    redirectToHomePage: boolean = true,
  ): void {
    this.#spinnerService.show();
    this.#authApi
      .changeApplicationContext$(applicationContext, organization?.id)
      .pipe(
        tap((applicationContextDto: ApplicationContextDto) => {
          // ustawienie contextu aplikacji
          this.#applicationContext.set(applicationContext);

          this.#organizationFromContext.set(organization);
          this.#lastUsedApplicationService.appCodes.set(
            applicationContextDto.ostatnioSkaldaneWnioski as Application[],
          );
          if (redirectToHomePage) {
            this.#router.navigate([APP_PATHS.home]);
          }
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas zmiany kontekstu aplikacji',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
      )
      .subscribe();
  }
}
